import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a freelancer invoice template include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A freelancer invoice template should include: your name/business name; your address and contact details; client name and address; unique invoice number; invoice date and payment due date; list of services with description, quantity, rate, and amount; subtotal, any applicable taxes, and the total amount due; payment instructions (bank details, UPI, PayPal, etc.).',
      },
    },
    {
      '@type': 'Question',
      name: 'Is a freelancer invoice template free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic offers a free freelancer invoice template you can fill in online and download as a professional PDF. No Word or Excel file to format. Up to 5 invoices per month free with no sign-up required.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I number my freelancer invoices?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Use a sequential numbering system, e.g. INV-001, INV-002 or CLIENTNAME-2024-01. Never reuse an invoice number. Consistent numbering makes accounting easier and looks professional to clients.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I add my logo to the freelancer invoice template?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic lets you upload your business logo when creating a freelancer invoice. It appears at the top of the PDF, making your invoice look professional and branded.',
      },
    },
  ],
};

export default function FreelancerInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Freelancer Invoice Template: Free PDF Download | Invilic"
        description="Free freelancer invoice template. Fill in online, download professional PDF instantly. Add logo, line items, payment terms. Better than Word or Excel. No sign-up needed."
        canonical="https://www.invilic.com/freelancer-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Freelancer Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A professional freelancer invoice template you can fill in online and download as a PDF in minutes for Free. No Word files, no Excel formulas. Just your details and a perfect invoice.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        {/* Template fields */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Freelancer Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Your name, business name & logo',
              'Your address & contact info',
              'Client name & address',
              'Invoice number & date',
              'Payment due date',
              'Service description, qty & rate',
              'Subtotal, tax & discount',
              'Total amount due',
              'Payment terms & notes',
              'Bank details or payment link',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-blue-100 dark:border-blue-800/30 bg-blue-50 dark:bg-blue-900/10">
                <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Steps */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Use the Freelancer Invoice Template
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click "Use Free Template" above. No account or download needed.' },
              { n: '2', title: 'Fill in your details', desc: 'Add your info, client info, services rendered, and rates. Takes 2 minutes.' },
              { n: '3', title: 'Download PDF', desc: 'Click download and get a professional PDF invoice instantly.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Freelancer Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Download Your Freelancer Invoice Now
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Free. Professional. Ready in 2 minutes.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}

